/**
 * CardEditor Component - Real-time Collaborative Rich Text Editor
 *
 * Uses Yjs CRDT + Tiptap for collaborative editing with:
 * - Real-time sync via WebSocket
 * - Offline editing support with IndexedDB
 * - Cursor presence (see other users' cursors)
 * - Markdown support
 *
 * Architecture: Yjs + Tiptap on Lambda + API Gateway WebSocket
 * Reference: EA_HEDGEDOC_SERVERLESS_ALTERNATIVES.md
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCursor from '@tiptap/extension-collaboration-cursor';
import * as Y from 'yjs';
import { IndexeddbPersistence } from 'y-indexeddb';
import { ApiGatewayYjsProvider } from '../providers/ApiGatewayYjsProvider';
import { getYjsWebSocketUrl } from '../services/api/endpoints';

interface CardEditorProps {
  cardId: string;
  userId: string;
  userName: string;
  cardType?: string;
  readOnly?: boolean;
  initialContent?: string;  // Initial markdown content to load into editor
  onSave?: (content: string) => void;
  onChange?: (content: string) => void;  // Called when content changes
}

interface EditorUser {
  id: string;
  name: string;
  color: string;
}

/**
 * Generate consistent color for user based on their ID
 */
function generateUserColor(userId: string): string {
  const colors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A',
    '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2',
    '#F8B195', '#C06C84', '#6C5B7B', '#355C7D'
  ];

  // Simple hash function to get consistent color for user
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
}

export const CardEditor: React.FC<CardEditorProps> = ({
  cardId,
  userId,
  userName,
  cardType = 'generic',
  readOnly = false,
  initialContent,
  onSave,
  onChange
}) => {
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
  const [syncStatus, setSyncStatus] = useState<'synced' | 'syncing' | 'offline'>('syncing');

  // Initialize Yjs document (CRDT)
  const ydoc = useMemo(() => new Y.Doc(), []);

  // Initialize API Gateway WebSocket provider for real-time sync
  const provider = useMemo(() => {
    const wsUrl = getYjsWebSocketUrl();
    const token = localStorage.getItem('idToken') || localStorage.getItem('token') || '';

    const yjsProvider = new ApiGatewayYjsProvider(
      wsUrl,
      cardId,
      ydoc,
      {
        token,
        userId,
        userName,
        userColor: generateUserColor(userId),
      }
    );

    // Connection status listeners (same events as y-websocket)
    yjsProvider.on('status', (event: [{ status: string }]) => {
      const status = event[0]?.status;
      if (status === 'connected') {
        setConnectionStatus('connected');
      } else if (status === 'disconnected') {
        setConnectionStatus('disconnected');
      } else if (status === 'connecting') {
        setConnectionStatus('connecting');
      }
    });

    yjsProvider.on('sync', (event: [boolean]) => {
      setSyncStatus(event[0] ? 'synced' : 'syncing');
    });

    return yjsProvider;
  }, [cardId, userId, userName, ydoc]);

  // Initialize IndexedDB persistence for offline support
  const indexeddbProvider = useMemo(() => {
    return new IndexeddbPersistence(`card-${cardId}`, ydoc);
  }, [cardId, ydoc]);

  // Initialize awareness for cursor presence
  useEffect(() => {
    if (provider.awareness) {
      provider.awareness.setLocalStateField('user', {
        id: userId,
        name: userName,
        color: generateUserColor(userId)
      });
    }

    return () => {
      if (provider.awareness) {
        provider.awareness.setLocalStateField('user', null);
      }
    };
  }, [provider, userId, userName]);

  // Initialize Tiptap editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      Collaboration.configure({
        document: ydoc,
      }),
      CollaborationCursor.configure({
        provider: provider,
        user: {
          id: userId,
          name: userName,
          color: generateUserColor(userId),
        },
      }),
    ],
    editable: !readOnly,
    content: '', // Content will be loaded from Yjs
  });

  // Initialize editor with content when Yjs document is empty
  useEffect(() => {
    if (editor && initialContent && ydoc) {
      // Wait for sync to complete before checking if document is empty
      const checkAndInitialize = () => {
        const yText = ydoc.getText('prosemirror');
        // Only initialize if document is empty (no existing content from Yjs)
        if (yText.length === 0) {
          console.log('[CardEditor] Initializing with content:', initialContent.substring(0, 100));
          editor.commands.setContent(initialContent);
        }
      };

      // Check after a brief delay to allow Yjs sync
      const timeout = setTimeout(checkAndInitialize, 500);
      return () => clearTimeout(timeout);
    }
  }, [editor, initialContent, ydoc]);

  // Notify parent of content changes
  useEffect(() => {
    if (editor && onChange) {
      const updateHandler = () => {
        const html = editor.getHTML();
        onChange(html);
      };

      editor.on('update', updateHandler);
      return () => {
        editor.off('update', updateHandler);
      };
    }
  }, [editor, onChange]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      provider?.destroy();
      indexeddbProvider?.destroy();
      editor?.destroy();
    };
  }, [provider, indexeddbProvider, editor]);

  // Handle save (export to Markdown)
  const handleSave = () => {
    if (editor && onSave) {
      const markdown = editor.getText(); // Get plain text (can use markdown extension for full markdown)
      onSave(markdown);
    }
  };

  // Get current online users
  const onlineUsers = useMemo(() => {
    if (!provider.awareness) return [];

    const states = provider.awareness.getStates();
    const users: EditorUser[] = [];

    states.forEach((state, clientId) => {
      if (state.user && clientId !== provider.awareness!.clientID) {
        users.push(state.user);
      }
    });

    return users;
  }, [provider.awareness]);

  // Don't render until editor is initialized
  if (!editor) {
    return (
      <div className="card-editor-container flex items-center justify-center min-h-screen">
        <div className="text-gray-600">Loading editor...</div>
      </div>
    );
  }

  return (
    <div className="card-editor-container">
      {/* Editor Toolbar */}
      <div className="editor-toolbar bg-gray-100 border-b border-gray-300 p-2 flex items-center justify-between">
        <div className="toolbar-buttons flex gap-2">
          <button
            onClick={() => editor?.chain().focus().toggleBold().run()}
            disabled={!editor || readOnly}
            className={`px-3 py-1 rounded ${
              editor?.isActive('bold') ? 'bg-blue-500 text-white' : 'bg-white'
            }`}
          >
            Bold
          </button>
          <button
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            disabled={!editor || readOnly}
            className={`px-3 py-1 rounded ${
              editor?.isActive('italic') ? 'bg-blue-500 text-white' : 'bg-white'
            }`}
          >
            Italic
          </button>
          <button
            onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
            disabled={!editor || readOnly}
            className={`px-3 py-1 rounded ${
              editor?.isActive('heading', { level: 2 }) ? 'bg-blue-500 text-white' : 'bg-white'
            }`}
          >
            H2
          </button>
          <button
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            disabled={!editor || readOnly}
            className={`px-3 py-1 rounded ${
              editor?.isActive('bulletList') ? 'bg-blue-500 text-white' : 'bg-white'
            }`}
          >
            List
          </button>
        </div>

        {/* Status Indicators */}
        <div className="status-indicators flex gap-4 items-center">
          {/* Sync Status */}
          <div className="flex items-center gap-2">
            <span
              className={`w-2 h-2 rounded-full ${
                syncStatus === 'synced'
                  ? 'bg-green-500'
                  : syncStatus === 'syncing'
                  ? 'bg-yellow-500 animate-pulse'
                  : 'bg-red-500'
              }`}
            />
            <span className="text-sm text-gray-600">{syncStatus}</span>
          </div>

          {/* Connection Status */}
          <div className="flex items-center gap-2">
            <span
              className={`w-2 h-2 rounded-full ${
                connectionStatus === 'connected'
                  ? 'bg-green-500'
                  : connectionStatus === 'connecting'
                  ? 'bg-yellow-500 animate-pulse'
                  : 'bg-red-500'
              }`}
            />
            <span className="text-sm text-gray-600">{connectionStatus}</span>
          </div>

          {/* Online Users */}
          {onlineUsers.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                {onlineUsers.length} user{onlineUsers.length > 1 ? 's' : ''} online
              </span>
              <div className="flex -space-x-2">
                {onlineUsers.map((user) => (
                  <div
                    key={user.id}
                    className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-white text-xs font-bold"
                    style={{ backgroundColor: user.color }}
                    title={user.name}
                  >
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Save Button */}
          {!readOnly && onSave && (
            <button
              onClick={handleSave}
              className="px-4 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Save
            </button>
          )}
        </div>
      </div>

      {/* Editor Content */}
      <div className="editor-content p-4 min-h-[400px] bg-white">
        <EditorContent editor={editor} />
      </div>

      {/* Card Type Template Info */}
      {cardType && (
        <div className="editor-footer bg-gray-50 border-t border-gray-300 p-2 text-sm text-gray-600">
          Card Type: <span className="font-semibold">{cardType}</span>
        </div>
      )}
    </div>
  );
};

export default CardEditor;
